import { useContext, useRef } from 'react';

import './CustomerLeadStepper.scss';

import { cNs } from 'src/helpers';
import { isCustomerStepStarted } from 'src/helpers/isCustomerStepStarted';
import { isCustomerStepSubmitted } from 'src/helpers/isCustomerStepSubmitted';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';

type CustomerLeadStepperProps = {
  isStartPage?: boolean;
};

export function CustomerLeadStepper({ isStartPage }: Readonly<CustomerLeadStepperProps>) {
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const currentStepRef = useRef<HTMLDivElement>(null);

  const { isEditing, customerStep, customerStepsInfo, customerLeadProduct, setCustomerStep } =
    useContext(CustomersContext);

  useDebounceEffect(
    () => {
      const currentStepPosition = currentStepRef.current?.getBoundingClientRect().left;

      if (currentStepPosition) {
        stepContainerRef.current?.scroll({
          left: currentStepPosition - 16, // - padding
          behavior: 'smooth',
        });
      }
    },
    [customerStep],
    100
  );

  return (
    <div
      className={cNs('customer-lead-stepper__container', isStartPage && 'customer-lead-stepper__container__start-page')}
      ref={stepContainerRef}
    >
      {customerStepsInfo.map((stepInfo, index) => {
        const isSelected = customerStep === stepInfo.step;

        const isStarted = isCustomerStepStarted(stepInfo.step, customerLeadProduct);
        const isSubmitted = isCustomerStepSubmitted(stepInfo.step, customerLeadProduct);
        const isLast = index === customerStepsInfo.length - 1;

        const handleClick =
          !isEditing && !isSelected
            ? () => {
                setCustomerStep(stepInfo.step);
              }
            : undefined;

        return (
          <div
            key={stepInfo.step}
            ref={isSelected ? currentStepRef : undefined}
            className={cNs(
              'customer-lead-stepper__step',
              isSelected && 'customer-lead-stepper__step-selected',
              isEditing && !isSelected && 'customer-lead-stepper__step-disabled',
              isSubmitted
                ? 'customer-lead-stepper__step-submitted'
                : isStarted
                  ? 'customer-lead-stepper__step-started'
                  : null
            )}
            onClick={handleClick}
          >
            {!isLast && <div className="customer-lead-stepper__step-arrow" />}
            {stepInfo.icon}
            <div className="flex column nowrap">
              <div className="customer-lead-stepper__step-number">Step {index + 1}</div>
              <div className="customer-lead-stepper__step-title">{stepInfo.description}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
